import React, { useState, useEffect } from "react";
import { PageHeader } from "CommonComponent";
import "../AppraisalSetup.css";
import { useDispatch, useSelector } from "react-redux";
import { Tabs, Tab } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import SetupCategoryContent from "./component/SetupCategoryContent";
import {
  getAppraisalById,
  toggleAppraisalSetupCategoryWindow,
  createSectionCategories,
  publishAppraisalSetup,
  resetAppraisalSetupFlow,
} from "actions";
import { isEmpty } from "lodash";
import {
  F,
  getAppraisalSetupSectionPivotRows,
  SECTION_CATEGORIES_PAYLOAD_KEY,
} from "pages/AppraisalManagment/constants/legacyApiKeys";
import {
  APPRAISAL_MAX_RATING_VALUE,
  normalizeSetupMaxRating,
} from "pages/AppraisalManagment/utils/appraisalProgressSummary";
import validate from "validate.js";

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const toIntegerWeightage = (value) => {
  const n = toNumber(value);
  if (n === null) return null;
  if (!Number.isInteger(n)) return null;
  return n;
};

const computeWeightageTotals = (categories = []) => {
  const weights = categories.map((c) => toIntegerWeightage(c?.weightage));
  const allValid = weights.every((w) => w !== null && w > 0);
  const total = weights.reduce((sum, w) => sum + (w || 0), 0);
  const remaining = 100 - total;
  return { total, remaining, allValid, isExact100: allValid && total === 100 };
};

const getCategoryValidationErrors = (categories = [], options = {}) => {
  const { requireExactTotal = true } = options;

  if (!categories || categories.length === 0) {
    return { _error: "At least one category is required." };
  }

  const fieldErrors = {};

  categories.forEach((category, index) => {
    const result = validate(
      {
        title: category.title,
        rating: category.rating,
        weightage: category.weightage,
        description: category.description,
      },
      schema
    );

    if (result) {
      fieldErrors[index] = result;
    }
  });

  if (requireExactTotal) {
    const totals = computeWeightageTotals(categories);
    if (totals.allValid && totals.total !== 100) {
      fieldErrors._totalWeightage = `Total question weightage (%)
 must be exactly 100 (currently ${totals.total})`;
    }
  }

  return Object.keys(fieldErrors).length > 0 ? fieldErrors : null;
};

const schema = {
  title: {
    presence: { allowEmpty: false, message: "^Title is required" },
    type: "string",
    length: {
      maximum: 255,
      message: "^Title must be less than 255 characters"
    }
  },
  rating: {
    presence: { allowEmpty: false, message: "^Rating is required" },
    inclusion: {
      within: [APPRAISAL_MAX_RATING_VALUE],
      message: "^Rating must be percentage (0-100%)",
    },
  },
  weightage: {
    presence: { allowEmpty: false, message: "^Weightage is required" },
    numericality: {
      onlyInteger: true,
      greaterThan: 0,
      lessThanOrEqualTo: 100,
      message: "^Weightage must be a whole number from 1 to 100"
    },
  }
};


const SetupCaregory = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const appraisalState = useSelector((state) => state.appraisalState);
  const session = useSelector((state) => state.session);

  const [setupRows, setSetupRows] = useState([]);
  const [activeKey, setActiveKey] = useState("default");

  const [categoriesBySetupId, setCategoriesBySetupId] = useState({});
  const [categoryErrors, setCategoryErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);

  // Determine if we're in edit mode (checking if categories already exist)
  const isEditMode =
    getAppraisalSetupSectionPivotRows(appraisalState.appraisalRecord).some(
      (row) => Array.isArray(row.categories) && row.categories.length > 0
    ) || location.pathname === '/appraisalSetup/update';

  const record = appraisalState.appraisalRecord;
  const isSubmitting = appraisalState.loading;
  const isAppraisalSetupUpdatePage = location.pathname === "/appraisalSetup/update";
  const isViewMode = location.pathname === "/appraisalSetup/view";
  const canEditRaw = record?.can_edit ?? record?.canEdit;
  const hasCanEditFromApi =
    canEditRaw !== undefined && canEditRaw !== null && String(canEditRaw).trim() !== "";
  const canEditSetup = hasCanEditFromApi ? canEditRaw === 1 || canEditRaw === "1" : true;
  const hideSaveAndPublish =
    isAppraisalSetupUpdatePage &&
    record &&
    String(record.is_active ?? "").toLowerCase() === "y" &&
    Number(record.is_published) === 1 &&
    String(record.is_deleted ?? "").toLowerCase() === "n";
  const isActivePublishedAppraisal =
    record &&
    String(record.is_active ?? "").toLowerCase() === "y" &&
    Number(record.is_published) === 1;

  useEffect(() => {
    if (appraisalState.appraisalRecord && appraisalState.appraisalRecord.id) {
      dispatch(getAppraisalById(appraisalState.appraisalRecord.id, "update"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isEmpty(appraisalState.appraisalRecord)) {
      const pivotRowsPayload = getAppraisalSetupSectionPivotRows(appraisalState.appraisalRecord);

      setSetupRows(pivotRowsPayload);
      setActiveKey(pivotRowsPayload[0]?.id || "default");

      const initialCategories = {};

      pivotRowsPayload.forEach((setupRow) => {
        const setupRowKey = setupRow.id;

        // Prefer saved categories when present
        if (Array.isArray(setupRow.categories) && setupRow.categories.length > 0) {
          initialCategories[setupRowKey] = [...setupRow.categories]
            .sort((a, b) => {
              const aOrder = Number(a?.sort_order);
              const bOrder = Number(b?.sort_order);
              const aHasOrder = Number.isFinite(aOrder);
              const bHasOrder = Number.isFinite(bOrder);
              if (aHasOrder && bHasOrder && aOrder !== bOrder) return aOrder - bOrder;
              if (aHasOrder !== bHasOrder) return aHasOrder ? -1 : 1;
              return 0;
            })
            .map((cat, catIndex) => ({
              recordId: `${Date.now()}-${Math.random()}`,
              [F.setupPivotKey]: setupRowKey,
              title: cat.title || "",
              rating: normalizeSetupMaxRating(cat.rating),
              weightage:
                cat.weightage != null && cat.weightage !== ""
                  ? String(Math.trunc(Number(cat.weightage)))
                  : null,
              description: cat.description || "",
              id: cat.id || null,
              sort_order: Number.isFinite(Number(cat.sort_order))
                ? Number(cat.sort_order)
                : catIndex + 1,
            }));
        } else {
          initialCategories[setupRowKey] = [{
            recordId: `${Date.now()}-${Math.random()}`,
            [F.setupPivotKey]: setupRowKey,
            title: "",
            rating: APPRAISAL_MAX_RATING_VALUE,
            weightage: null,
            description: "",
            sort_order: 1,
          }];
        }
      });

      setCategoriesBySetupId(initialCategories);
    }
  }, [appraisalState.appraisalRecord]);


  useEffect(() => { 
    if (!isEmpty(appraisalState.validation_error)) { 
      setCategoryErrors(appraisalState.validation_error);
    }
  }, [appraisalState.validation_error]);

  const handleCategoryChange = (setupTabId, newCategories) => {
    setCategoriesBySetupId((prev) => ({ ...prev, [setupTabId]: newCategories }));
  };

  const activeTabIndex = setupRows.findIndex((setupRow) => String(setupRow.id) === String(activeKey));
  const activeSetupId = activeTabIndex >= 0 ? setupRows[activeTabIndex]?.id : null;
  const isLastTab = activeTabIndex >= 0 && activeTabIndex === setupRows.length - 1;

  const isCurrentTabValidForNext = (() => {
    if (!activeSetupId) return false;
    const validationErrors = getCategoryValidationErrors(categoriesBySetupId[activeSetupId] || [], { requireExactTotal: false });
    return !validationErrors;
  })();

  const validateForm = () => {
    const newErrors = {};

    for (const [setupTabId, categories] of Object.entries(categoriesBySetupId)) {
      const tabErrors = getCategoryValidationErrors(categories, { requireExactTotal: true });
      if (tabErrors) {
        newErrors[setupTabId] = tabErrors;
      }
    }
    setCategoryErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateCurrentTabForNext = () => {
    if (!activeSetupId) return false;

    const currentValidationErrors = getCategoryValidationErrors(categoriesBySetupId[activeSetupId] || [], { requireExactTotal: false });
    if (currentValidationErrors) {
      setCategoryErrors((prev) => ({ ...prev, [activeSetupId]: currentValidationErrors }));
      return false;
    }

    setCategoryErrors((prev) => {
      if (!prev[activeSetupId]) return prev;
      const newErrors = { ...prev };
      delete newErrors[activeSetupId];
      return newErrors;
    });
    return true;
  };

  const handleNextTab = () => {
    if (!isViewMode && !validateCurrentTabForNext()) return;
    if (activeTabIndex >= 0 && activeTabIndex < setupRows.length - 1) {
      setActiveKey(setupRows[activeTabIndex + 1].id);
    }
  };

  // Live validity until each setup tab reaches 100% weightage total
  useEffect(() => {
    const newErrors = {};
    for (const [setupTabId, categories] of Object.entries(categoriesBySetupId)) {
      const tabErrors = getCategoryValidationErrors(categories, { requireExactTotal: true });
      if (tabErrors) {
        newErrors[setupTabId] = tabErrors;
      }
    }
    setCategoryErrors(newErrors);
    setIsFormValid(Object.keys(newErrors).length === 0);
  }, [categoriesBySetupId]);

  const clearFieldError = (setupTabId, categoryIndex, field) => {
    setCategoryErrors((prev) => {
      if (!prev[setupTabId] || !prev[setupTabId][categoryIndex]) return prev;
      const newErrors = { ...prev };

      const catErrors = { ...newErrors[setupTabId][categoryIndex] };
      delete catErrors[field];

      if (Object.keys(catErrors).length === 0) {
        delete newErrors[setupTabId][categoryIndex];
      } else {
        newErrors[setupTabId][categoryIndex] = catErrors;
      }

      if (Object.keys(newErrors[setupTabId]).length === 0) {
        delete newErrors[setupTabId];
      }

      return newErrors;
    });
  };


  const handleBack = () => {
    if (activeTabIndex > 0) {
      setActiveKey(setupRows[activeTabIndex - 1].id);
      return;
    }
    dispatch(toggleAppraisalSetupCategoryWindow(false));
  };

  const handleCloseView = () => {
    navigate("/appraisalSetup", { replace: true });
    queueMicrotask(() => {
      dispatch(resetAppraisalSetupFlow());
    });
  };

  const goToSetupList = () => {
    navigate("/appraisalSetup", { replace: true });
    queueMicrotask(() => {
      dispatch(resetAppraisalSetupFlow());
    });
  };

  const handleSubmit = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (validateForm()) {
      if (!appraisalState.appraisalRecord || !appraisalState.appraisalRecord.id) {
        console.error('Appraisal record is not available');
        return;
      }
      if (isEditMode && !canEditSetup) {
        return;
      }
      try {
        const object_viewed_id =
          session?.current_page_permissions && !Array.isArray(session.current_page_permissions)
            ? session.current_page_permissions.object_id
            : "";
        const data = {
          object_viewed_id,
          appraisal_setup_id: appraisalState.appraisalRecord.id,
          [SECTION_CATEGORIES_PAYLOAD_KEY]: Object.entries(categoriesBySetupId).map(
            ([section_setup_id, categories]) => ({
              section_setup_id,
              categories: categories.map((category, index) => ({
                ...category,
                sort_order: index + 1,
              })),
            })
          ),
        };
        dispatch(createSectionCategories(data, true))
          .then(() => {
            goToSetupList();
          })
          .catch((error) => {
            console.error("Error submitting section categories:", error);
          });
      } catch (error) {
        console.error('Error submitting section categories:', error);
      }
    }
  };

  const handlePublish = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (validateForm()) {
      if (!appraisalState.appraisalRecord || !appraisalState.appraisalRecord.id) {
        console.error('Appraisal record is not available');
        return;
      }
      // First save the categories, then publish
      try {
        const object_viewed_id =
          session?.current_page_permissions && !Array.isArray(session.current_page_permissions)
            ? session.current_page_permissions.object_id
            : "";
        const data = {
          object_viewed_id,
          appraisal_setup_id: appraisalState.appraisalRecord.id,
          [SECTION_CATEGORIES_PAYLOAD_KEY]: Object.entries(categoriesBySetupId).map(
            ([section_setup_id, categories]) => ({
              section_setup_id,
              categories: categories.map((category, index) => ({
                ...category,
                sort_order: index + 1,
              })),
            })
          ),
        };
        // Save categories first (skip action redirect), then publish, then leave
        dispatch(createSectionCategories(data, true))
          .then(() => dispatch(publishAppraisalSetup(appraisalState.appraisalRecord.id)))
          .then(() => {
            goToSetupList();
          })
          .catch((error) => {
            console.error('Error saving section categories before publishing:', error);
          });
      } catch (error) {
        console.error('Error preparing to publish:', error);
      }
    }
  };

  return (
    <>
      <PageHeader title="Appraisal Setup Form — Setup Category" />

      <div className="setup-category page-card">
        {setupRows.length > 0 ? (
          <Tabs activeKey={activeKey} onSelect={(k) => setActiveKey(k)}>
            {setupRows.map((setupTab) => {
              const hasErrors = !!categoryErrors[setupTab.id];
              const tabTitle =
                setupTab?.section?.title ??
                setupTab?.[F.pivotRelation]?.title ??
                setupTab?.[F.titleSynonym] ??
                "Section";
              return (
                <Tab
                  eventKey={setupTab.id}
                  key={setupTab.id}
                  disabled={String(activeKey) !== String(setupTab.id)}
                  title={
                    <span className={hasErrors ? "" : ""}>
                      {tabTitle}
                    </span>
                  }
                  tabClassName={hasErrors ? "tab-error" : ""}
                >
                  <SetupCategoryContent
                    setupRecordId={setupTab.id}
                    categories={categoriesBySetupId[setupTab.id] || []}
                    setCategories={(newCats) => handleCategoryChange(setupTab.id, newCats)}
                    errors={categoryErrors[setupTab.id] || {}}
                    clearFieldError={(catIndex, field) => clearFieldError(setupTab.id, catIndex, field)}
                    totals={computeWeightageTotals(categoriesBySetupId[setupTab.id] || [])}
                    showAddMore={!isActivePublishedAppraisal && !isViewMode && (!isEditMode || canEditSetup)}
                    isReadOnly={isViewMode || (isEditMode && !canEditSetup)}
                  />
                  {categoryErrors[setupTab.id]?._error && (
                    <p className="text-danger mt-2">{categoryErrors[setupTab.id]._error}</p>
                  )}
                  {categoryErrors[setupTab.id]?._totalWeightage && (
                    <p className="text-danger mt-2">{categoryErrors[setupTab.id]._totalWeightage}</p>
                  )}
                </Tab>
              );
            })}
          </Tabs>

        ) : (
          <div className="empty-state py-5">
            <p className="empty-state__title">No Sections Selected</p>
            <p className="empty-state__message">
              Go back to the previous step and select at least one section to continue.
            </p>
          </div>
        )}
      </div>

      <div className="mt-4 d-flex gap-2">
        <button type="button" className="c-b-btn" style={{ marginRight: 0 }} onClick={handleBack}>
          <img className="me-2" src="/assets/images/back-arrow.svg" alt="back" />
          Back
        </button>
        {!isLastTab ? (
          <button
            type="button"
            className="n-u-btn"
            onClick={handleNextTab}
            disabled={!isViewMode && !isCurrentTabValidForNext}
          >
            Next <img className="ms-2" src="/assets/images/next-icon.svg" alt="next" />
          </button>
        ) : isViewMode ? (
          <button type="button" className="n-u-btn" onClick={handleCloseView}>
            Close
          </button>
        ) : isEditMode ? (
          <>
            <button type="button" className="n-u-btn" onClick={handleSubmit} disabled={!isFormValid || !canEditSetup || isSubmitting}>
              Save <img className="ms-2" src="/assets/images/next-icon.svg" alt="next" />
            </button>
            {!hideSaveAndPublish && (
              <button
                type="button"
                className="n-u-btn"
                style={{ backgroundColor: '#dc3545', borderColor: '#dc3545', color: '#FFFFFF' }}
                onClick={handlePublish}
                disabled={!isFormValid || !canEditSetup || isSubmitting}
              >
                Save & Publish
              </button>
            )}
          </>
        ) : (
          <>
            <button type="button" className="n-u-btn" onClick={handleSubmit} disabled={!isFormValid || isSubmitting}>
              Save <img className="ms-2" src="/assets/images/next-icon.svg" alt="next" />
            </button>
            {!hideSaveAndPublish && (
              <button type="button" className="n-u-btn" style={{ backgroundColor: '#dc3545', borderColor: '#dc3545', color: '#FFFFFF' }} onClick={handlePublish} disabled={!isFormValid || isSubmitting}>
                Save & Publish
              </button>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default SetupCaregory;
